import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash2 } from "react-feather";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import SupplierAdvanceModal from "../../core/modals/finance/SupplierAdvanceModal";
import { fetchSupplierAdvances, deleteSupplierAdvanceThunk } from "../../core/redux/slices/financeSlice";
import { fetchSuppliers } from "../../core/redux/slices/supplierSlice";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const SupplierAdvance = () => {
  const dispatch = useDispatch();
  const { advances, loading } = useSelector((s) => s.finance);
  const suppliers = useSelector((s) => s.suppliers.list);
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchSupplierAdvances());
  }, [dispatch]);

  const handleSearch = () => dispatch(fetchSupplierAdvances());

  const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.supplierName }));

  // The /api/SupplierPayment/advances/all endpoint accepts no query params — date filtering is applied client-side.
  const data = useMemo(() => {
    const q = search.toLowerCase();
    return advances.filter((a) => {
      const matchSearch = !q || a.supplierName?.toLowerCase().includes(q) || a.advanceEntryNo?.toLowerCase().includes(q);
      const matchSupplier = !supplierFilter || a.supplierID === supplierFilter;
      const d = a.entryDate ? a.entryDate.split("T")[0] : null;
      const matchDate = (!dateFrom || !d || d >= dateFrom) && (!dateTo || !d || d <= dateTo);
      return matchSearch && matchSupplier && matchDate;
    });
  }, [advances, search, supplierFilter, dateFrom, dateTo]);

  const openAdd = () => { setEditRecord(null); setShowModal(true); };
  const openEdit = (rec) => { setEditRecord(rec); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditRecord(null); dispatch(fetchSupplierAdvances()); };

  const columns = [
    { title: "Date", dataIndex: "entryDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—", sorter: (a, b) => (a.entryDate || "").localeCompare(b.entryDate || "") },
    { title: "Supplier", dataIndex: "supplierName", sorter: (a, b) => (a.supplierName || "").localeCompare(b.supplierName || "") },
    { title: "Payment Mode", dataIndex: "paymentType", width: 120 },
    { title: "Reference", dataIndex: "advanceEntryNo", width: 150 },
    {
      title: "Amount", dataIndex: "amount", align: "right", width: 120,
      render: fmt,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: "Action", dataIndex: "action", width: 100,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="me-2 p-2 btn btn-sm" onClick={() => openEdit(rec)}><Edit size={15} /></button>
          <button className="p-2 btn btn-sm text-danger"
            onClick={() => confirmDelete(() => dispatch(deleteSupplierAdvanceThunk(rec.advanceID ?? rec.id)), `advance for ${rec.supplierName}`)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Supplier Advances" subtitle="Track advance payments to suppliers" onAdd={openAdd} addLabel="Record Advance" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} style={{ width: 140 }} />
              </div>
              <button className="btn btn-sm btn-primary" onClick={handleSearch}>Search</button>
              <div style={{ minWidth: 180 }}>
                <Select classNamePrefix="react-select" options={supplierOptions}
                  value={supplierOptions.find((o) => o.value === supplierFilter) || null}
                  placeholder="All Suppliers"
                  isClearable
                  onChange={(opt) => setSupplierFilter(opt?.value || "")} />
              </div>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search supplier or reference..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <SupplierAdvanceModal show={showModal} onClose={closeModal} editRecord={editRecord} />
    </div>
  );
};

export default SupplierAdvance;
